import { useEffect, useState, type ReactNode } from 'react';
import { EncounterKind, EncounterName } from '@runner/simulation';
import { usePrelaunch } from '../data/launch';
import { IconFinish, IconFork, IconPuddle, color } from '@runner/ui';
import encBarrier from '../../../../brand/exports/encounters/encounter-barrier-ui.svg?url';
import encBurning from '../../../../brand/exports/encounters/encounter-burning-barrier-ui.svg?url';
import encWater from '../../../../brand/exports/encounters/encounter-water-station-ui.svg?url';
import encShoe from '../../../../brand/exports/encounters/encounter-golden-shoe-ui.svg?url';
import encForge from '../../../../brand/exports/encounters/encounter-forge-gate-ui.svg?url';
import encFinish from '../../../../brand/exports/encounters/encounter-finish-ui.svg?url';
import type { CourseObjectView, FeedItem, WorldView } from '../data/types';
import { EXHIBITION_LABEL, big, exitPreview, phaseExplanation, phaseLabel, nextRunFunding, runDecides } from '../data/model';
import { compact, secs, short, units } from '../lib/format';
import { More } from './More';

export function ModeBadge({ view }: { view: WorldView }) {
  return (
    <span className={`badge ${view.mode === 'demo' ? 'demo' : view.mode === 'local' ? 'local' : 'warn'}`} title={view.modeLabel}>
      <span className="dot" aria-hidden="true" />
      {view.mode === 'demo'
        ? 'DEMO · simulated'
        : view.mode === 'local'
          ? view.network?.kind === 'mainnet'
            ? `LIVE · ${view.network.name}`
            : view.network?.kind === 'testnet'
              ? `TESTNET · ${view.network.name}`
              : 'LOCAL CHAIN · test assets'
          : 'OFFLINE'}
    </span>
  );
}

/** Seconds until the protocol's own countdown target, or null when no honest countdown exists. */
export function countdown(view: WorldView): number | null {
  const t = view.timing;
  if (t.countdownTo === null || view.freshness.stale) return null;
  return Math.max(0, t.countdownTo - t.now);
}

export function PhasePill({ view }: { view: WorldView }) {
  const left = countdown(view);
  const what = view.phase === 'preview' ? 'steering opens' : 'window closes';
  return (
    <span className="badge phase" aria-live="off" data-testid="phase-pill" data-phase={view.phase} data-stage={view.stage} data-exhibition={view.run?.exhibition ? 'yes' : undefined}>
      {phaseLabel(view.phase, view.stage, !!view.run?.exhibition)}
      {left !== null && (
        <span className="muted" data-testid="countdown">
          · {what} {secs(left)}
        </span>
      )}
      {left === null && view.freshness.stale && view.mode !== 'demo' && <span className="muted">· no countdown while data is stale</span>}
      {left === null && !view.freshness.stale && view.phase === 'funding' && <span className="muted">· no countdown until the provider answers</span>}
      {left === null && !view.freshness.stale && view.stage === 'catch-up' && <span className="muted">· waiting for permissionless resolution</span>}
    </span>
  );
}

/** Compact freshness indicator for the top bar (LOCAL CHAIN mode only). Text, not just colour. */
/** `network`: on mainnet the header shows this one chip instead of a separate LIVE · <chain> badge; the chain name and
 *  block number are hidden on phones (the dot carries the state). */
export function FreshnessBadge({ view, network }: { view: WorldView; network?: string }) {
  const f = view.freshness;
  const prelaunch = usePrelaunch();
  // An outage shorter than the banner's grace reads as a calm "Reconnecting"; it turns red when it lasts.
  const brief = useHeldFor(f.api === 'down') < DOWN_GRACE_MS;
  if (f.api === 'not-used') return null;
  if (prelaunch && f.api !== 'ok') {
    return (
      <span className="badge fresh connecting" data-testid="freshness" data-state="prelaunch">
        <span className="dot" aria-hidden="true" />
        Launching soon
      </span>
    );
  }
  const state = f.api === 'down' ? 'down' : f.api === 'connecting' ? 'connecting' : f.catchingUp || f.api === 'degraded' ? 'catching-up' : f.feed !== 'live' ? 'feed-reconnecting' : 'live';
  const text =
    state === 'down'
      ? brief
        ? 'Reconnecting'
        : 'Offline'
      : state === 'connecting'
        ? 'Connecting…'
        : state === 'catching-up'
          ? 'Syncing'
          : state === 'feed-reconnecting'
            ? 'Syncing'
            : `Live · #${f.blockNumber ?? '?'}`;
  return (
    <span className={`badge fresh ${state === 'down' && brief ? 'connecting' : state}`} data-testid="freshness" data-state={state} data-feed={f.feed} data-api={f.api} title={network ? `${network} · block ${f.blockNumber ?? '?'}${f.note ? ` · ${f.note}` : ''}` : f.note}>
      <span className="dot" aria-hidden="true" />
      {network && state === 'live' ? (
        <>
          <span className="label-long">
            Live · {network} · #{f.blockNumber ?? '?'}
          </span>
          <span className="label-short">Live</span>
        </>
      ) : (
        <>
          <span className="label-long">{network ? `${text} · ${network}` : text}</span>
          <span className="label-short">{text}</span>
        </>
      )}
    </span>
  );
}

/** Milliseconds `active` has been continuously true (0 while false), re-evaluated every second. */
function useHeldFor(active: boolean): number {
  const [since, setSince] = useState<number | null>(null);
  const [, tick] = useState(0);
  useEffect(() => {
    setSince(active ? Date.now() : null);
    if (!active) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  return since === null ? 0 : Date.now() - since;
}

/** Players hear only about problems that last: syncing and reconnecting blips show as the amber header chip alone;
 *  a banner appears once the server has been unreachable for DOWN_GRACE_MS, or syncing has lasted SLOW_GRACE_MS.
 *  The technical cause sits behind "Details". */
const DOWN_GRACE_MS = 15_000;
const SLOW_GRACE_MS = 120_000;

export function ConnectionBanner({ view }: { view: WorldView }) {
  const f = view.freshness;
  const prelaunch = usePrelaunch();
  const used = f.api !== 'not-used' && !(prelaunch && f.api !== 'ok');
  const live = f.api === 'ok' && !f.catchingUp && f.feed === 'live';
  const down = used && f.api === 'down';
  const downFor = useHeldFor(down);
  const slowFor = useHeldFor(used && !live && !down);
  if (!used || live) return null;
  if (down ? downFor < DOWN_GRACE_MS : slowFor < SLOW_GRACE_MS) return null;
  return (
    <div className={`conn-banner ${down ? 'down' : 'catching-up'}`} role="status" data-testid="connection-banner" data-state={down ? 'down' : f.catchingUp || f.api === 'degraded' ? 'catching-up' : f.api === 'connecting' ? 'connecting' : 'feed'}>
      <strong>{down ? "Can't reach the game server." : 'Still syncing with the chain.'}</strong>{' '}
      {down ? 'Retrying — the game picks up where it left off, and your tokens are on-chain either way.' : 'The game will update in a moment.'}{' '}
      {f.note && (
        <details>
          <summary>Details</summary>
          {f.note}
        </details>
      )}
    </div>
  );
}

/** Phase of the shared run in one sentence, from protocol facts. */
export function PhaseExplainer({ view }: { view: WorldView }) {
  return (
    <p className="small" style={{ margin: '8px 0 0' }} data-testid="phase-explainer" data-phase={view.phase}>
      {phaseExplanation(view.phase, view.stage, view.funding, view.replayRunId, !!view.run?.exhibition && view.run.status !== 'finished')}
    </p>
  );
}

/** This window's buying vs selling, in quote dollars: "Bulls $42 · Bears $18". */
function bullsBears(view: WorldView): string {
  const o = view.openInput;
  const p = view.market.price;
  if (!o || !(p > 0) || o.volume === 0n) return 'No trades this window';
  const buys = (o.volume + o.net) / 2n;
  const sells = (o.volume - o.net) / 2n;
  const dollars = (r: bigint) => `$${((Number(r) / 1e18) * p).toFixed(r > 0n && Number(r) / 1e18 * p < 10 ? 2 : 0)}`;
  return `Bulls ${dollars(buys)} · Bears ${dollars(sells)}`;
}

/** Net steering of the open window in plain terms (ppm of reference supply, capped at ±300 like the contract). */
export function steerState(view: WorldView): { u: number; cap: number; share: number; steering: boolean; say: string; funded: number | null } {
  const o = view.openInput;
  const cap = 300;
  const u = o ? Number(o.net / (view.refSupply / 1_000_000n)) : 0;
  const share = Math.min(1, Math.abs(u) / cap) * 50;
  const steering = view.phase === 'running' && view.stage === 'input-open' && !view.freshness.stale;
  // Between runs the bar shows how far trading fees have funded the next one, so a trade visibly moves something.
  const next = steering ? null : nextRunFunding(view);
  const say = !steering
    ? view.phase === 'preview'
      ? 'Steering opens in a moment'
      : next
        ? `Next run ${next.pct}% funded · ~$${Math.max(1, Math.ceil(next.volumeToGo))} of trading to go`
        : view.phase === 'funding'
          ? 'Next run is starting'
          : 'Waiting for the next run'
    : `${bullsBears(view)}${u > 0 ? ' → heading right' : u < 0 ? ' → heading left' : ' → straight ahead'}`;
  return { u, cap, share, steering, say, funded: next ? next.pct : null };
}

/** Unresolved influence of the open control window. Normalized like the contract: ppm of reference supply. */
export function PressureMeter({ view }: { view: WorldView }) {
  const o = view.openInput;
  const ppmUnit = view.refSupply / 1_000_000n;
  const u = o ? Number(o.net / ppmUnit) : 0;
  const cap = 300;
  const w = Math.min(50, (Math.abs(u) / cap) * 50);
  const yours = o ? Number(o.yours / ppmUnit) : 0;
  const yoursPos = 50 + Math.max(-50, Math.min(50, (yours / cap) * 50));
  const steering = view.phase === 'running' && view.stage === 'input-open' && !view.freshness.stale;
  return (
    <div className={steering ? undefined : 'pressure-inactive'} data-testid="pressure" data-steering={steering ? 'yes' : 'no'}>
      <div className="pressure" role="meter" aria-valuemin={-cap} aria-valuemax={cap} aria-valuenow={u} aria-label="Unresolved steering input this window">
        <div className="mid" />
        {u !== 0 && <div className={`fill ${u > 0 ? 'right' : 'left'}`} style={{ width: `${w}%` }} />}
        {yours !== 0 && <div className="yours" style={{ left: `${yoursPos}%` }} title="Your contribution" />}
      </div>
      <div className="pressure-labels">
        <span style={{ color: 'var(--link)' }}>← Sells · Forge</span>
        <span>{u === 0 ? 'neutral' : `${u > 0 ? '+' : '−'}${Math.min(cap, Math.abs(u))}${Math.abs(u) > cap ? ' (capped)' : ''}`}</span>
        <span style={{ color: 'var(--harvest-text)' }}>Buys · Harvest →</span>
      </div>
      <p className="small muted" style={{ margin: '6px 0 0' }}>
        Net this window: {o ? compact(o.net, 18) : '0'} RUNNER ({o?.swaps ?? 0} swaps)
        {o && o.yours !== 0n && (
          <strong data-testid="your-input">
            {' '}
            · yours {o.yours > 0n ? '+' : ''}
            {compact(o.yours, 18)} ({o.yours > 0n ? 'right' : 'left'})
          </strong>
        )}
        . Only net counts, capped at ±{cap}.
        {!steering && <strong> This window does not steer a step{view.freshness.stale ? ' (data stale)' : ''}.</strong>}
      </p>
    </div>
  );
}

/** Encounter badges: the brand's UI-form encounter art (brand/exports/encounters/*-ui.svg); icons where it has none. */
const ENCOUNTER_ART: Record<number, string> = {
  [EncounterKind.BARRIER]: encBarrier,
  [EncounterKind.BURNING_BARRICADE]: encBurning,
  [EncounterKind.WATER_STATION]: encWater,
  [EncounterKind.GOLDEN_SHOE]: encShoe,
  [EncounterKind.FORGE_GATE]: encForge,
  [EncounterKind.FINISH]: encFinish,
};

export function EncounterIcon({ kind, size = 24 }: { kind: number; size?: number }) {
  const art = ENCOUNTER_ART[kind];
  const icon = art ? (
    <img src={art} width={size + 8} height={size + 8} alt="" />
  ) : kind === EncounterKind.FORK ? (
    <IconFork size={size} />
  ) : kind === EncounterKind.PUDDLE ? (
    <IconPuddle size={size} />
  ) : (
    <IconFinish size={size} />
  );
  return (
    <span className={`enc-icon${art ? ' art' : ''}`} aria-hidden="true">
      {icon}
    </span>
  );
}

/** What an object does — in change order 4.0 terms: only the run's own road budget can move. */
export function effectText(o: CourseObjectView, view: WorldView): { effect: string; who: string; funded: string } {
  const q = view.quote.symbol;
  const dec = view.quote.decimals;
  const road = view.road;
  const cohort = view.run?.cohortEpoch;
  switch (o.kind) {
    case EncounterKind.FORK:
      return {
        effect: 'Right → Harvest route. Left → Forge route.',
        who: 'Decides where this run’s leftover money goes at the finish: stakers (Harvest) or the Forge.',
        funded: 'This run’s money only — never fee rewards.',
      };
    case EncounterKind.BARRIER:
      return { effect: 'Hit: −30% pace for 2 steps, no shoe pickup.', who: 'No money moves.', funded: '—' };
    case EncounterKind.PUDDLE:
      return { effect: 'Hit: −10% pace for 1 step.', who: 'No money moves.', funded: '—' };
    case EncounterKind.BURNING_BARRICADE:
      return {
        effect: 'Hit: Overheat, −20% pace for 3 steps.',
        who: road
          ? `Sends ${units(big(road.overheatPerStep), dec)} ${q} of this run’s money to the Forge each step for up to ${road.params.overheatBiasSteps} steps (max ${units(big(road.params.overheatCap), dec)} ${q}).`
          : 'Sends a capped slice of this run’s money to the Forge.',
        funded: 'This run’s money only — fee rewards and prizes are untouched.',
      };
    case EncounterKind.WATER_STATION:
      return {
        effect: 'Clears slowdowns and ends Overheat.',
        who: road
          ? `${units(big(road.waterSlice), dec)} ${q} of this run’s money to stakers${cohort !== undefined ? ` (round ${cohort})` : ''}, and unlocks their held-back rewards.`
          : 'A slice of this run’s money to stakers.',
        funded: 'From this run’s money.',
      };
    case EncounterKind.GOLDEN_SHOE: {
      const s = view.run?.shoe;
      return {
        effect: 'Reach the gold zone without stumbling to collect.',
        who: 'One buyer wins; buying more while entries are open counts for more.',
        funded: o.funded && s ? `${units(s.amount, dec)} ${q}, paid for before it appeared.` : 'No prize this run.',
      };
    }
    case EncounterKind.FORGE_GATE:
      return {
        effect: 'Unlocks a buy-back and burn of RUNNER.',
        who: 'Paid from the Forge’s own reserve — not this run’s money or stakers.',
        funded: `Up to 10 ${q}, if the reserve holds it.`,
      };
    case EncounterKind.FINISH:
      return {
        effect: 'Ends the run.',
        who: 'Leftover money follows the route RUNNER took, and held-back rewards unlock.',
        funded: 'Balances and claims carry over.',
      };
    default:
      return { effect: '', who: '', funded: '' };
  }
}

const PACE = 150;

export function NextEncounters({ view, max = 3 }: { view: WorldView; max?: number }) {
  const run = view.run;
  if (!run || run.status === 'awaiting-randomness') return <p className="muted small">No course yet. {phaseExplanation(view.phase, view.stage, view.funding, view.replayRunId)}</p>;
  const last = view.steps[view.steps.length - 1];
  const y = last ? Number(last.state.y) : 0;
  const upcoming = run.course.filter((o) => Number(o.y) > y && o.kind !== EncounterKind.MERGE).slice(0, max);
  if (!upcoming.length) return <p className="muted small">Nothing left on this course.</p>;
  return (
    <ul className="coming-up">
      {upcoming.map((o) => {
        const e = effectText(o, view);
        const stepsAway = Math.max(1, Math.ceil((Number(o.y) - y) / PACE));
        const unfunded = o.kind === EncounterKind.GOLDEN_SHOE && !o.funded;
        return (
          <li className="encounter" key={o.index}>
            <EncounterIcon kind={o.kind} />
            <div>
              <strong>{EncounterName[o.kind]}</strong>
              <span className="small muted"> · in ~{stepsAway} step{stepsAway === 1 ? '' : 's'}{unfunded ? ' · no prize this run' : ''}</span>
              <div className="small">{e.effect}</div>
              {e.funded !== '—' && <div className="small muted">{e.who}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** One-line "what is next" under the world on narrow layouts, where the side panel sits below the fold. */
/** Which side of the road an object sits on (lanes are fixed: right = Harvest, left = Forge). */
function sideOf(o: CourseObjectView): string {
  const mid = (Number(o.xL) + Number(o.xR)) / 2;
  return mid > 150 ? 'right' : mid < -150 ? 'left' : 'middle';
}

/** Dollar-style amount of a raw quote value, e.g. "$0.80". */
function usd(v: bigint, decimals: number): string {
  return `$${units(v, decimals, 2)}`;
}

/**
 * What's coming and what's at stake, in plain words: the next object on the road, the money each lane decides at a
 * fork, and where the next Golden Shoe is. Lanes are fixed (forks never mirror): buys steer right toward the Harvest
 * (the run's leftover money to stakers), sells steer left toward the Forge (buyback and burn).
 */
export function NextChip({ view }: { view: WorldView }) {
  const run = view.run;
  if (!run) return null;
  const last = view.steps[view.steps.length - 1];
  const y = last ? Number(last.state.y) : 0;
  const ahead = run.course.filter((c) => Number(c.y) > y && c.kind !== EncounterKind.MERGE);
  const o = ahead[0];
  if (!o) return null;
  const dec = view.quote.decimals;
  const d = runDecides(view.road, view.overheat);
  const leftover = d?.funded ? d.unassigned : 0n;
  const stepsAway = (c: CourseObjectView) => Math.max(1, Math.ceil((Number(c.y) - y) / PACE));
  const when = (c: CourseObjectView) => `~${stepsAway(c)} step${stepsAway(c) === 1 ? '' : 's'}`;
  let line: ReactNode;
  switch (o.kind) {
    case EncounterKind.FORK:
      line = (
        <>
          <span className="lane right">Right (bulls): {leftover > 0n ? `${usd(leftover, dec)} to stakers` : 'Harvest'}</span>
          <span className="lane left">Left (bears): {leftover > 0n ? `${usd(leftover, dec)} to the burn` : 'Forge'}</span>
        </>
      );
      break;
    case EncounterKind.FORGE_GATE:
      line = <span className="lane left">Hit = buyback and burn</span>;
      break;
    case EncounterKind.WATER_STATION:
      line = <span className="lane right">{d?.funded ? `${usd(d.waterSlice, dec)} to stakers` : 'Clears slowdowns'}</span>;
      break;
    case EncounterKind.BURNING_BARRICADE:
      line = <span className="lane left">Hit = Overheat, run money burns</span>;
      break;
    case EncounterKind.GOLDEN_SHOE:
      line = <span className="lane gold">{run.shoe && o.funded ? `${usd(run.shoe.amount, dec)} prize · ${sideOf(o)} · buy on runner.party to enter` : 'No prize this run'}</span>;
      break;
    default:
      line = <span className="small">{effectText(o, view).effect}</span>;
  }
  // Shoe radar: the next funded Golden Shoe further down the road, if the next object is not it.
  const shoe = o.kind === EncounterKind.GOLDEN_SHOE ? null : ahead.find((c) => c.kind === EncounterKind.GOLDEN_SHOE && c.funded);
  return (
    <details className="next-chip" data-testid="next-chip" aria-label="Next on the road" onKeyDown={e => {
      if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); }
    }}>
      <summary>
      <EncounterIcon kind={o.kind} size={22} />
        <strong>
          {o.kind === EncounterKind.FORK ? 'Fork' : EncounterName[o.kind]} in {when(o)}
        </strong>
        <span className="next-chevron" aria-hidden="true">⌄</span>
      </summary>
      <div className="next-expanded">
        <div className="next-stakes">{line}</div>
        {shoe && run.shoe && (
          <div className="small next-shoe" data-testid="shoe-radar">
            👟 {usd(run.shoe.amount, dec)} shoe in {when(shoe)} ({sideOf(shoe)})
          </div>
        )}
      </div>
    </details>
  );
}

/** Raw-unit amount with its unit, carrying the exact raw value for verification (data-raw). */
export function Amount({ v, decimals, symbol, dp = 2, testid }: { v: bigint | string; decimals: number; symbol?: string; dp?: number; testid?: string }) {
  const b = big(v);
  return (
    <span data-testid={testid} data-raw={b.toString()} className="amount">
      {units(b, decimals, dp)}
      {symbol && <span className="muted small"> {symbol}</span>}
    </span>
  );
}

/**
 * "What this run decides": only the run's reserved road budget. Shown apart from base rewards, which no run can touch.
 */
export function RunDecidesPanel({ view }: { view: WorldView }) {
  const q = view.quote;
  const run = view.run;
  const d = runDecides(view.road, view.overheat);
  const f = view.funding;
  const route = view.steps.length ? view.steps[view.steps.length - 1].state : null;
  if (run && d && (d.exhibition || (run.exhibition && !d.funded))) {
    // D48: never shown as a funded run with 0, and never as warm-up.
    return (
      <section className="panel" aria-labelledby="decides-h" data-testid="run-decides" data-funded="exhibition">
        <h2 id="decides-h">What this run decides</h2>
        <p data-testid="exhibition-run" style={{ marginTop: 0 }}>
          <strong>{EXHIBITION_LABEL}.</strong>
        </p>
        <p className="small">
          The road reserve could not fund run {run.id}, and the active configuration has no minimum run budget, so it plays anyway with a budget of 0.
          Water, Overheat and the finish assign nothing; no road money moves. A Golden Shoe is still funded from the prize reserve, and Forge gates still
          authorize buybacks from the existing Forge reserve. Base holder income never depends on the run.
        </p>
        <dl className="kv">
          <dt>Road budget</dt>
          <dd>
            <Amount v={0n} decimals={q.decimals} symbol={q.symbol} testid="road-budget" /> (exhibition)
          </dd>
          <dt>Execution provision</dt>
          <dd>{view.road?.escrow && big(view.road.escrow.provision) > 0n ? `${units(big(view.road.escrow.provision), q.decimals)} ${q.symbol} from operations` : 'none — operations could not cover it'}</dd>
        </dl>
      </section>
    );
  }
  if (!run || !d || !d.funded) {
    return (
      <section className="panel" aria-labelledby="decides-h" data-testid="run-decides" data-funded="no">
        <h2 id="decides-h">Next run’s money</h2>
        <p className="small" style={{ marginTop: 0 }}>
          A run starts once its money is set aside. Until then, nothing is paid out.
        </p>
        {f && (
          <dl className="kv">
            <dt>Road reserve</dt>
            <dd>
              <Amount v={f.roadReserve} decimals={q.decimals} symbol={q.symbol} testid="road-reserve" />
            </dd>
            <dt>Run budget / minimum</dt>
            <dd>
              {units(big(f.runBudget), q.decimals)} / {units(big(f.minRunBudget), q.decimals)} {q.symbol}
            </dd>
            <dt>Operations / provision per run</dt>
            <dd>
              {units(big(f.operations), q.decimals)} / {units(big(f.opsProvision), q.decimals)} {q.symbol}
            </dd>
            <dt>Next run</dt>
            <dd data-testid="fundable">
              {f.fundable
                ? `fundable (${units(big(f.nextBudget), q.decimals)} ${q.symbol})`
                : f.exhibition
                  ? `exhibition run — no road budget at stake (${f.reason.replace(/-/g, ' ')})`
                  : `not fundable — ${f.reason.replace(/-/g, ' ')}`}
            </dd>
          </dl>
        )}
      </section>
    );
  }
  const pct = (x: bigint) => (d.budget > 0n ? Number((x * 10_000n) / d.budget) / 100 : 0);
  const rest = d.open ? d.unassigned : (d.returned ?? 0n);
  return (
    <section className="panel" aria-labelledby="decides-h" data-testid="run-decides" data-funded="yes" data-consistent={d.consistent ? 'yes' : 'no'}>
      <h2 id="decides-h">Where this run’s money goes</h2>
      <p className="small" style={{ marginTop: 0 }}>
        <strong>{units(d.budget, q.decimals)} {q.symbol}</strong> is in play. Only things on the road move it.
      </p>
      <div className="alloc-bar" role="img" aria-label={`Run budget: ${pct(d.toHolders)}% to depositors, ${pct(d.toForge)}% to the Forge, ${pct(rest)}% ${d.open ? 'still open' : 'back to the reserve'}`}>
        <div style={{ width: `${pct(d.toHolders)}%`, background: color.harvestSoft }}>{pct(d.toHolders) >= 12 ? `${pct(d.toHolders).toFixed(0)}%` : ''}</div>
        <div style={{ width: `${pct(d.toForge)}%`, background: color.forgeSoft }}>{pct(d.toForge) >= 12 ? `${pct(d.toForge).toFixed(0)}%` : ''}</div>
        <div style={{ width: `${pct(rest)}%`, background: color.paper }}>{pct(rest) >= 12 ? `${pct(rest).toFixed(0)}%` : ''}</div>
      </div>
      <div className="legend">
        <span>
          <i style={{ background: color.harvestSoft }} />
          Depositors
        </span>
        <span>
          <i style={{ background: color.forgeSoft }} />
          Forge
        </span>
        <span>
          <i style={{ background: color.paper }} />
          {d.open ? 'Still open' : 'Back to reserve'}
        </span>
      </div>
      {d.open && route && <p className="small muted">{exitPreview(route.route, route.inSplit)}</p>}
      {!d.open && run.exitRoute && (
        <p className="small muted">
          Closed on the {run.exitRoute} route{run.reachedFinish === false ? ' at the step bound (no exit allocation)' : ''}.
        </p>
      )}
      {d.bias?.active && (
        <p className="small" style={{ margin: '6px 0 0' }}>
          <strong>Overheat live:</strong> {units(d.overheatPerStep, q.decimals)} {q.symbol}/step to Forge, {d.bias.stepsLeft} step{d.bias.stepsLeft === 1 ? '' : 's'} left.
        </p>
      )}
      {/* A finished run's split is its result, so it stays open; while running the bar carries it. */}
      <More label="Breakdown" open={!d.open}>
        <dl className="kv">
          <dt>This run’s money</dt>
          <dd>
            <Amount v={d.budget} decimals={q.decimals} symbol={q.symbol} testid="road-budget" />
          </dd>
          <dt>To depositors (round {d.cohortEpoch})</dt>
          <dd>
            <Amount v={d.toHolders} decimals={q.decimals} symbol={q.symbol} testid="road-to-holders" />
          </dd>
          <dt>To the Forge</dt>
          <dd>
            <Amount v={d.toForge} decimals={q.decimals} symbol={q.symbol} testid="road-to-forge" />
          </dd>
          {d.open ? (
            <>
              <dt>Still open</dt>
              <dd>
                <Amount v={d.unassigned} decimals={q.decimals} symbol={q.symbol} testid="road-unassigned" />
              </dd>
            </>
          ) : (
            <>
              <dt>Back to reserve</dt>
              <dd>
                <Amount v={d.returned ?? 0n} decimals={q.decimals} symbol={q.symbol} testid="road-returned" />
              </dd>
            </>
          )}
          {d.open && (
            <>
            <dt>Water, per hit</dt>
            <dd>
              {units(d.waterSlice, q.decimals)} {q.symbol} → depositors
            </dd>
            <dt>Overheat bias</dt>
            <dd data-testid="overheat" data-active={d.bias?.active ? 'yes' : 'no'}>
              {d.bias?.active
                ? `live: ${units(d.overheatPerStep, q.decimals)}/step to Forge, ${d.bias.stepsLeft} step${d.bias.stepsLeft === 1 ? '' : 's'} left, ${units(d.bias.assigned, q.decimals)} of ${units(d.overheatCap, q.decimals)} cap used`
                : `none (on hit: ${units(d.overheatPerStep, q.decimals)}/step × ${d.overheatSteps}, cap ${units(d.overheatCap, q.decimals)})`}
            </dd>
            </>
          )}
        </dl>
        <p className="note" style={{ marginBottom: 0 }}>
          The Forge never takes fee rewards, claims, prizes or deposits — only this run’s money.
        </p>
      </More>
    </section>
  );
}

/** Base holder income of the current epoch — the five-bucket waterfall, independent of any run. */
export function BaseRewardsPanel({ view }: { view: WorldView }) {
  const b = view.baseRewards;
  const q = view.quote;
  if (!b) return null;
  const p = b.policy;
  const est = b.openPeriodSplitEstimate;
  const holders = big(est.holdersDirect) + big(est.holdersDeferred);
  return (
    <section className="panel" aria-labelledby="base-h" data-testid="base-rewards">
      <h2 id="base-h">Fee rewards</h2>
      <p className="small" style={{ marginTop: 0 }}>
        Depositors earn part of every trade’s fee. The run doesn’t change this.
      </p>
      <dl className="kv">
        <dt>Fees this round ({b.epoch})</dt>
        <dd>
          <Amount v={b.openPeriodAccrued} decimals={q.decimals} symbol={q.symbol} testid="base-accrued" />
        </dd>
        <dt>To depositors (est.)</dt>
        <dd>
          <Amount v={holders} decimals={q.decimals} symbol={q.symbol} dp={4} testid="base-holders-estimate" />
        </dd>
        <dt>To future runs</dt>
        <dd>
          {units(big(est.road), q.decimals, 4)} {q.symbol}
        </dd>
        {b.cohortHolderCredit && (
          <>
            <dt>This run’s depositors (round {b.cohortEpoch})</dt>
            <dd>
              {units(big(b.cohortHolderCredit.direct) + big(b.cohortHolderCredit.deferred), q.decimals, 4)} {q.symbol}
            </dd>
          </>
        )}
      </dl>
      <More label="How this works">
        <p>
          Fees split by policy #{p.policyId}: depositors {p.holdersBps / 100}% · Forge {p.forgeBps / 100}% · road {p.roadBps / 100}% · prize {p.prizeBps / 100}% · ops{' '}
          {p.opsBps / 100}%.
        </p>
        <p>
          Of the depositors’ part, {100 - p.holderDeferredBps / 100}% is paid when the round closes and {p.holderDeferredBps / 100}% is held back until a water station, a finish or the deadline.
        </p>
        <p>Paid only from fees actually received — not a yield, rate or minimum. Every deposit earns by its size, Runner or not.</p>
      </More>
    </section>
  );
}

/** Run identity: what it is pinned to. */
export function RunPins({ view }: { view: WorldView }) {
  const r = view.run;
  if (!r) return null;
  return (
    <div className="pins" data-testid="run-pins">
      <span className="tag info">Run {r.id}</span>
      <span className="tag" data-testid="engine-version">
        engine v{r.engineVersion ?? '?'}
      </span>
      <span className="tag">config v{r.configVersion}</span>
      <span className="tag" data-testid="cohort-epoch">
        sealed cohort: epoch {r.cohortEpoch}
      </span>
      <span className="tag">policy #{r.policyId}</span>
      {r.exhibition && (
        <span className="tag" data-testid="exhibition-tag">
          {EXHIBITION_LABEL}
        </span>
      )}
      {r.abandoned && <span className="tag blocked">{r.exhibition ? 'abandoned' : 'abandoned — budget returned'}</span>}
    </div>
  );
}

const TONE: Record<string, string> = {
  harvest: color.harvest,
  forge: color.forge,
  gold: color.gold,
  water: color.water,
  warn: color.orange,
  neutral: color.paperDeep,
};

export function Feed({ items, max = 30 }: { items: FeedItem[]; max?: number }) {
  return (
    <ul className="feed" aria-label="Recent events">
      {items.length === 0 && <li className="muted empty">No events yet.</li>}
      {items.slice(0, max).map((f) => (
        <li key={f.id} data-seq={f.seq} data-kind={f.kind}>
          <span className="dot" style={{ background: TONE[f.tone ?? 'neutral'] }} aria-hidden="true" />
          <span>
            {f.text}
            {f.evidence && <span className="mono muted"> {short(f.evidence)}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}
